/**
 * Conversations Page
 * Unified view of all interactions across channels with filters
 */

import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Search, Filter, LayoutGrid, List, MessageSquare, MapPin, X } from 'lucide-react';
import AnalysisCard from '../components/dashboard/AnalysisCard';
import AnalysisDetailModal from '../components/dashboard/AnalysisDetailModal';
import DateRangeFilter from '../components/dashboard/DateRangeFilter';
import RefreshButton from '../components/shared/RefreshButton';
import EmptyState from '../components/shared/EmptyState';
import Pagination from '@shared/components/Pagination';
import useDateRange from '../hooks/useDateRange';
import usePagination from '@shared/hooks/usePagination';
import useDebounce from '../hooks/useDebounce';
import { getAnalysesList } from '../services/sentimentService';
import { getAreaInteractionIds, getLocationsByInteractionIds } from '../services/locationService';

const ConversationsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialFrom = searchParams.get('from_date');
  const initialTo = searchParams.get('to_date');
  const { from, to, onChange } = useDateRange(
    initialFrom && initialTo ? { from: initialFrom, to: initialTo } : undefined
  );
  const { page, pageSize, setPage, setPageSize } = usePagination(1, 20);

  const [loading, setLoading] = useState(true);
  const [analyses, setAnalyses] = useState([]);
  const [totalAnalyses, setTotalAnalyses] = useState(0);
  const [selectedAnalysis, setSelectedAnalysis] = useState(null);
  const [viewMode, setViewMode] = useState('grid');
  const [locationsById, setLocationsById] = useState({});

  // Filters (initial search from URL params e.g. ?search=xxx)
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const debouncedSearch = useDebounce(searchTerm, 400);
  const [channelFilter, setChannelFilter] = useState('');
  const [sentimentFilter, setSentimentFilter] = useState('');
  const [callTypeFilter, setCallTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [cityFilter, setCityFilter] = useState(searchParams.get('city') || '');

  const clearCityFilter = () => {
    setCityFilter('');
    setPage(1);
    const next = new URLSearchParams(searchParams);
    next.delete('city');
    setSearchParams(next, { replace: true });
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const params = { page, page_size: pageSize, from_date: from, to_date: to };
      if (debouncedSearch) params.search = debouncedSearch;
      if (channelFilter) params.interaction_type = channelFilter;
      if (sentimentFilter) params.sentiment = sentimentFilter;
      if (callTypeFilter) params.contact_reason = callTypeFilter;
      if (statusFilter) params.disposition = statusFilter;

      if (cityFilter) {
        const idsRes = await getAreaInteractionIds(cityFilter, { start_date: from, end_date: to });
        if (!idsRes.success) {
          toast.error(idsRes.error);
          setAnalyses([]);
          setTotalAnalyses(0);
          return;
        }
        const ids = idsRes.data.interaction_ids || [];
        if (ids.length === 0) {
          setAnalyses([]);
          setTotalAnalyses(0);
          return;
        }
        params.interaction_ids = ids.join(',');
      }

      const result = await getAnalysesList(params);
      if (result.success) {
        const rows = result.data.analyses || [];
        setAnalyses(rows);
        setTotalAnalyses(result.data.total || 0);
        const ids = rows.map(a => a.interaction_id).filter(Boolean);
        if (ids.length > 0) {
          const locRes = await getLocationsByInteractionIds(ids);
          setLocationsById(locRes.success ? (locRes.data.locations || {}) : {});
        } else {
          setLocationsById({});
        }
      } else {
        toast.error(result.error);
      }
    } catch (err) {
      toast.error('Failed to load conversations');
    } finally {
      setLoading(false);
    }
  }, [from, to, page, pageSize, debouncedSearch, channelFilter, sentimentFilter, callTypeFilter, statusFilter, cityFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  return (
    <div>
      {/* Controls bar */}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div className="flex items-center space-x-3">
          <RefreshButton onClick={fetchData} loading={loading} />
          <span className="text-sm text-gray-500">
            {totalAnalyses} conversation{totalAnalyses !== 1 ? 's' : ''}
          </span>
          {cityFilter && (
            <span className="inline-flex items-center space-x-1 bg-primary-50 text-primary-700 text-xs font-medium px-2.5 py-1 rounded-full border border-primary-200">
              <MapPin size={12} />
              <span>City: {cityFilter}</span>
              <button
                type="button"
                onClick={clearCityFilter}
                aria-label="Clear city filter"
                className="ml-1 hover:text-primary-900"
              >
                <X size={12} />
              </button>
            </span>
          )}
        </div>
        <div className="flex items-center space-x-3">
          {/* Search */}
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
              placeholder="Search conversations..."
              className="pl-8 pr-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 w-36 sm:w-52"
            />
          </div>
          {/* Channel filter */}
          <div className="relative">
            <Filter size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
            <select
              value={channelFilter}
              onChange={(e) => { setChannelFilter(e.target.value); setPage(1); }}
              className="pl-8 pr-8 py-1.5 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 appearance-none"
            >
              <option value="">All Channels</option>
              <option value="call">Call</option>
              <option value="chat">Chat</option>
              <option value="email">Email</option>
            </select>
          </div>
          {/* Sentiment filter */}
          <select
            value={sentimentFilter}
            onChange={(e) => { setSentimentFilter(e.target.value); setPage(1); }}
            className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="">All Sentiments</option>
            <option value="very_positive">Very Positive</option>
            <option value="positive">Positive</option>
            <option value="neutral">Neutral</option>
            <option value="negative">Negative</option>
            <option value="very_negative">Very Negative</option>
          </select>
          {/* Call type filter */}
          <select
            value={callTypeFilter}
            onChange={(e) => { setCallTypeFilter(e.target.value); setPage(1); }}
            className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="">All Types</option>
            <option value="general">General</option>
            <option value="billing">Billing</option>
            <option value="technical">Technical</option>
            <option value="complaint">Complaint</option>
            <option value="inquiry">Inquiry</option>
            <option value="feedback">Feedback</option>
          </select>
          {/* Call status filter */}
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
            className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="">All Status</option>
            <option value="pending">Pending</option>
            <option value="resolved">Resolved</option>
            <option value="follow_up_required">Follow Up Required</option>
          </select>
          {/* View toggle */}
          <div className="flex items-center space-x-1 border border-gray-300 rounded-lg p-0.5" role="group" aria-label="View mode">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-1.5 rounded transition-colors ${viewMode === 'grid' ? 'bg-primary-100 text-primary-700' : 'text-gray-400 hover:text-gray-600'}`}
              aria-label="Grid view"
              aria-pressed={viewMode === 'grid'}
            >
              <LayoutGrid size={14} />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-1.5 rounded transition-colors ${viewMode === 'list' ? 'bg-primary-100 text-primary-700' : 'text-gray-400 hover:text-gray-600'}`}
              aria-label="List view"
              aria-pressed={viewMode === 'list'}
            >
              <List size={14} />
            </button>
          </div>
          <DateRangeFilter from={from} to={to} onChange={onChange} />
        </div>
      </div>

      {/* Conversation cards */}
      {analyses.length === 0 ? (
        <EmptyState
          icon={MessageSquare}
          title="No conversations found"
          description="Conversations are generated automatically when calls are processed. Try adjusting the filters or date range."
        />
      ) : viewMode === 'grid' ? (
        <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(min(380px, 100%), 1fr))' }}>
          {analyses.map(analysis => (
            <AnalysisCard
              key={analysis.id}
              analysis={analysis}
              compact
              onViewDetails={setSelectedAnalysis}
              location={locationsById[analysis.interaction_id]}
            />
          ))}
        </div>
      ) : (
        <div className="space-y-3">
          {analyses.map(analysis => (
            <AnalysisCard
              key={analysis.id}
              analysis={analysis}
              onViewDetails={setSelectedAnalysis}
              location={locationsById[analysis.interaction_id]}
            />
          ))}
        </div>
      )}

      <Pagination
        page={page}
        pageSize={pageSize}
        total={totalAnalyses}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        loading={loading}
      />

      {/* Detail Modal */}
      {selectedAnalysis && (() => {
        const idx = analyses.findIndex(a => a.id === selectedAnalysis.id);
        return (
          <AnalysisDetailModal
            analysis={selectedAnalysis}
            onClose={() => setSelectedAnalysis(null)}
            hasPrev={idx > 0}
            hasNext={idx < analyses.length - 1}
            location={locationsById[selectedAnalysis.interaction_id]}
            onNavigate={(dir) => {
              const next = analyses[idx + dir];
              if (next) setSelectedAnalysis(next);
            }}
          />
        );
      })()}
    </div>
  );
};

export default ConversationsPage;
